// ================================================================================
// StrategicOverviewPanel.jsx — Surplus / Canvassing / Resonance tabbed container
// ================================================================================

import React, { useState, useEffect } from "react";
import {
    Sparkles, Wallet, ArrowRight, Users, Brain, BookOpen, Eye,
    GitMerge, AlertOctagon, Layers, Calendar, MousePointer,
    Clock, Timer, Info
} from "lucide-react";
import { formatChannelName } from "./helpers";

const StrategicOverviewPanel = ({ results, currentWardId }) => {
    const [activeStrategicTab, setActiveStrategicTab] = useState(null);
    const [selectedCanvassWeek, setSelectedCanvassWeek] = useState(null);

    const resonanceData = results?.resonance_strategic_overlay;
    const currentWardResonance = resonanceData?.ward_level_resonance?.[currentWardId];
    const expansionOps = results?.campaign_optimization?.budget_expansion_opportunities;
    const expansionRecs = expansionOps?.recommendations ?? [];

    const stratCanvass = expansionRecs.find(r => r.channel === 'strategic_canvassing');
    const surplusFinancialRecs = expansionRecs.filter(r => r.channel !== 'strategic_canvassing');

    const hasSurplus = expansionOps?.status === 'OPPORTUNITY_DETECTED';
    const hasCanvass = !!stratCanvass;
    const hasResonance = !!currentWardResonance;

    useEffect(() => {
        if (!activeStrategicTab) {
            if (hasSurplus) setActiveStrategicTab('surplus');
            else if (hasCanvass) setActiveStrategicTab('canvassing');
            else if (hasResonance) setActiveStrategicTab('resonance');
        }
    }, [hasSurplus, hasCanvass, hasResonance, activeStrategicTab]);

    const getTabTheme = () => {
        if (activeStrategicTab === 'surplus') return { border: 'border-emerald-500/30', bg: 'bg-gradient-to-br from-emerald-900/20 to-slate-900/50' };
        if (activeStrategicTab === 'canvassing') return { border: 'border-blue-500/30', bg: 'bg-blue-900/10' };
        if (activeStrategicTab === 'resonance') return { border: 'border-indigo-500/20', bg: 'bg-slate-900/30' };
        return { border: 'border-slate-700', bg: 'bg-slate-800' };
    };
    const tabTheme = getTabTheme();

    if (!hasSurplus && !hasCanvass && !hasResonance) return null;

    return (
        <div className={`panel p-6 ${tabTheme.bg} border ${tabTheme.border} relative overflow-hidden transition-all duration-300`}>

            {/* TAB HEADERS */}
            <div className="grid grid-cols-3 gap-4 mb-6 relative z-10">
                {hasSurplus && (
                    <button
                        onClick={() => setActiveStrategicTab('surplus')}
                        className={`p-3 rounded-xl border text-left flex flex-col justify-center transition-all duration-200 ${activeStrategicTab === 'surplus' ? 'bg-emerald-500/20 border-emerald-500/50 shadow-[0_0_15px_-3px_rgba(16,185,129,0.2)]' : 'bg-slate-800/40 border-slate-700/30 hover:bg-slate-700/50 hover:border-slate-600'}`}
                    >
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className={`p-1.5 rounded-lg ${activeStrategicTab === 'surplus' ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-slate-400'}`}><Sparkles size={16} /></div>
                            <span className={`text-xs font-bold uppercase tracking-wide ${activeStrategicTab === 'surplus' ? 'text-emerald-300' : 'text-slate-400'}`}>Capital</span>
                        </div>
                        <div className={`text-sm font-semibold truncate w-full ${activeStrategicTab === 'surplus' ? 'text-white' : 'text-slate-300'}`}>Surplus Deployment</div>
                    </button>
                )}

                {hasCanvass && (
                    <button
                        onClick={() => setActiveStrategicTab('canvassing')}
                        className={`p-3 rounded-xl border text-left flex flex-col justify-center transition-all duration-200 ${activeStrategicTab === 'canvassing' ? 'bg-blue-500/20 border-blue-500/50 shadow-[0_0_15px_-3px_rgba(59,130,246,0.2)]' : 'bg-slate-800/40 border-slate-700/30 hover:bg-slate-700/50 hover:border-slate-600'}`}
                    >
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className={`p-1.5 rounded-lg ${activeStrategicTab === 'canvassing' ? 'bg-blue-500 text-white' : 'bg-slate-700 text-slate-400'}`}><Users size={16} /></div>
                            <span className={`text-xs font-bold uppercase tracking-wide ${activeStrategicTab === 'canvassing' ? 'text-blue-300' : 'text-slate-400'}`}>Tactical</span>
                        </div>
                        <div className={`text-sm font-semibold truncate w-full ${activeStrategicTab === 'canvassing' ? 'text-white' : 'text-slate-300'}`}>Strategic Canvassing</div>
                    </button>
                )}

                {hasResonance && (
                    <button
                        onClick={() => setActiveStrategicTab('resonance')}
                        className={`p-3 rounded-xl border text-left flex flex-col justify-center transition-all duration-200 ${activeStrategicTab === 'resonance' ? 'bg-indigo-500/20 border-indigo-500/50 shadow-[0_0_15px_-3px_rgba(99,102,241,0.2)]' : 'bg-slate-800/40 border-slate-700/30 hover:bg-slate-700/50 hover:border-slate-600'}`}
                    >
                        <div className="flex items-center gap-2 mb-1.5">
                            <div className={`p-1.5 rounded-lg ${activeStrategicTab === 'resonance' ? 'bg-indigo-500 text-white' : 'bg-slate-700 text-slate-400'}`}><Brain size={16} /></div>
                            <span className={`text-xs font-bold uppercase tracking-wide ${activeStrategicTab === 'resonance' ? 'text-indigo-300' : 'text-slate-400'}`}>Overlay</span>
                        </div>
                        <div className={`text-sm font-semibold truncate w-full ${activeStrategicTab === 'resonance' ? 'text-white' : 'text-slate-300'}`}>Resonance Strategy</div>
                    </button>
                )}
            </div>

            {/* TAB CONTENT */}
            <div className="relative z-10 min-h-[300px]">

                {/* 1. SURPLUS CAPITAL DEPLOYMENT */}
                {activeStrategicTab === 'surplus' && hasSurplus && (
                    <div className="animate-in fade-in zoom-in-95 duration-300">
                        <div className="flex items-center gap-2 mb-6 p-3 bg-slate-800/60 rounded border border-slate-700/50">
                            <div className="bg-slate-700 p-1.5 rounded text-slate-400"><Wallet size={14} /></div>
                            <div className="text-xs text-slate-300">
                                Available Surplus: <span className="text-white font-mono font-bold">£{(expansionOps.surplus_available ?? 0).toLocaleString()}</span>
                            </div>
                            <div className="ml-auto text-[10px] font-bold bg-emerald-500 text-slate-900 px-2 py-0.5 rounded-full uppercase tracking-wider">Tactical Financial</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {surplusFinancialRecs.map((rec, idx) => (
                                <div key={idx} className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4 hover:border-emerald-500/40 transition-colors group">
                                    <div className="flex justify-between items-start mb-3">
                                        <h5 className="font-bold text-white flex items-center gap-2">
                                            {formatChannelName(rec.channel)}
                                            <ArrowRight size={14} className="text-slate-500 group-hover:text-emerald-400 transition-colors" />
                                        </h5>
                                        <span className="text-xs font-mono text-emerald-400 bg-emerald-900/20 px-2 py-0.5 rounded border border-emerald-500/20">
                                            £{(rec.suggested_spend ?? 0).toLocaleString()}
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-300 mb-4 leading-relaxed h-10 overflow-hidden">{rec.rationale}</p>
                                    <div className="flex items-center justify-between pt-3 border-t border-slate-700/50">
                                        <div className="flex flex-col">
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Est. Impact</span>
                                            <span className="text-sm font-bold text-white">+{rec.projected_votes} Votes</span>
                                        </div>
                                        <div className="flex flex-col items-end">
                                            <span className="text-[10px] text-slate-500 uppercase tracking-wider">Efficiency</span>
                                            <span className="text-sm font-mono text-slate-300">£{rec.projected_cost_per_vote?.toFixed(2)} CPV</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 2. STRATEGIC CANVASSING */}
                {activeStrategicTab === 'canvassing' && hasCanvass && (
                    <div className="animate-in fade-in zoom-in-95 duration-300 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            {/* LEFT COLUMN */}
                            <div className="flex flex-col justify-between gap-4">
                                <div>
                                    <h6 className="text-xs font-semibold text-slate-300 uppercase tracking-wider mb-2">Operational Rationale</h6>
                                    <p className="text-sm text-slate-300 leading-relaxed">{stratCanvass.rationale}</p>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-slate-800/60 p-3 rounded border border-slate-700/50 flex flex-col justify-center">
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Target Invest.</div>
                                        <div className="text-lg font-bold text-white font-mono leading-none">
                                            {stratCanvass.resource_note ? (stratCanvass.resource_note.match(/\d+(\.\d+)?/)?.[0] || '0') : '0'}
                                            <span className="text-xs font-normal text-slate-400"> Hrs</span>
                                        </div>
                                    </div>
                                    <div className="bg-slate-800/60 p-3 rounded border border-slate-700/50 flex flex-col justify-center">
                                        <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Est. Impact</div>
                                        <div className="text-lg font-bold text-white font-mono leading-none">
                                            +{stratCanvass.projected_votes} <span className="text-xs font-normal text-slate-400">Votes</span>
                                        </div>
                                    </div>
                                    <div className="bg-blue-900/10 p-3 rounded border border-blue-500/20 flex flex-col justify-center relative overflow-hidden">
                                        <div className="text-[10px] text-blue-300/70 uppercase tracking-wider mb-1">Standard Window</div>
                                        <div className="text-xs font-bold text-blue-100 leading-snug">{stratCanvass.recommended_time_window || 'Standard Hours'}</div>
                                    </div>
                                </div>

                                {stratCanvass.timing_rationale && (
                                    <div className="pt-2">
                                        <h6 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                                            <Timer size={12} /> Timing Logic
                                        </h6>
                                        <p className="text-xs text-slate-400 leading-relaxed border-l-2 border-slate-700 pl-3 italic">{stratCanvass.timing_rationale}</p>
                                    </div>
                                )}
                            </div>

                            {/* RIGHT COLUMN: Flighting Schedule Chart */}
                            {stratCanvass.weekly_schedule && (
                                <div className="bg-slate-800/40 p-4 rounded border border-slate-700/50 flex flex-col h-full relative">
                                    <div className="flex justify-between items-start mb-3">
                                        <h6 className="text-xs font-semibold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                                            <Calendar size={14} /> Flighting Schedule
                                        </h6>
                                        <div className="text-[10px] text-slate-500 flex items-center gap-1">
                                            <MousePointer size={10} /> Click bars for tactical detail
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-x-auto pb-2 custom-scrollbar flex items-end min-h-[160px]">
                                        <div className="flex items-end gap-2 h-40 pt-4 px-2 min-w-max w-full">
                                            {(() => {
                                                const scheduleEntries = Object.entries(stratCanvass.weekly_schedule);
                                                const maxHours = Math.max(...scheduleEntries.map(([, data]) =>
                                                    typeof data === 'object' ? (data.hours || 0) : data
                                                )) || 1;

                                                return scheduleEntries.map(([week, data]) => {
                                                    const hours = typeof data === 'object' ? (data.hours || 0) : data;
                                                    const isSelected = selectedCanvassWeek === week;

                                                    return (
                                                        <button
                                                            key={week}
                                                            onClick={() => setSelectedCanvassWeek(isSelected ? null : week)}
                                                            className="flex flex-col items-center gap-2 group w-12 h-full justify-end focus:outline-none"
                                                        >
                                                            <div className="w-full flex-1 bg-slate-700/20 rounded-t-lg relative flex items-end overflow-hidden transition-all duration-300">
                                                                <div
                                                                    className={`w-full rounded-t transition-all duration-300 relative ${isSelected ? 'bg-gradient-to-t from-blue-500 to-cyan-200 opacity-100 shadow-[0_0_15px_rgba(34,211,238,0.4)]' : 'bg-gradient-to-t from-blue-600 to-cyan-400 opacity-80 group-hover:opacity-100'}`}
                                                                    style={{ height: `${Math.max((hours / maxHours) * 100, 4)}%` }}
                                                                >
                                                                    {isSelected && <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-white rounded-full"></div>}
                                                                </div>
                                                            </div>
                                                            <div className="flex flex-col items-center space-y-0.5">
                                                                <span className={`text-[10px] font-medium uppercase tracking-tight transition-colors ${isSelected ? 'text-white font-bold' : 'text-slate-500'}`}>
                                                                    {week.replace(/Week\s?|W/i, 'W')}
                                                                </span>
                                                                <span className={`text-xs font-mono transition-colors ${isSelected ? 'text-cyan-400 font-bold' : 'text-slate-200'}`}>
                                                                    {hours.toFixed(0)}<span className="text-[9px] font-normal ml-0.5 opacity-50">h</span>
                                                                </span>
                                                            </div>
                                                        </button>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>

                                    {selectedCanvassWeek && stratCanvass.weekly_schedule[selectedCanvassWeek] && typeof stratCanvass.weekly_schedule[selectedCanvassWeek] === 'object' && (
                                        <div className="mt-3 p-3 bg-slate-900/60 border-t border-blue-500/30 rounded-b-lg animate-in slide-in-from-top-2 fade-in duration-200">
                                            {(() => {
                                                const details = stratCanvass.weekly_schedule[selectedCanvassWeek];
                                                return (
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between items-start">
                                                            <span className="text-xs font-bold text-white uppercase tracking-wide">{selectedCanvassWeek} Specifics</span>
                                                            {details.window && (
                                                                <span className="text-[10px] font-mono text-cyan-300 bg-cyan-900/20 px-1.5 py-0.5 rounded border border-cyan-500/20 flex items-center gap-1">
                                                                    <Clock size={10} /> {details.window}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {details.rationale_short && <p className="text-xs text-slate-300 leading-snug">"{details.rationale_short}"</p>}
                                                        <div className="flex items-center gap-4 pt-1">
                                                            {details.volunteers_needed && (
                                                                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                                    <Users size={12} className="text-blue-400" />
                                                                    <span className="text-white font-mono font-bold">{details.volunteers_needed}</span>
                                                                    <span>Volunteers Required</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {stratCanvass.resource_note && (
                            <div className="text-xs text-blue-300/80 italic border-t border-blue-500/20 pt-3">
                                <Info size={12} className="inline mr-1 mb-0.5" />
                                Note: {stratCanvass.resource_note}
                            </div>
                        )}
                    </div>
                )}

                {/* 3. RESONANCE STRATEGIC OVERLAY */}
                {activeStrategicTab === 'resonance' && hasResonance && (
                    <div className="animate-in fade-in zoom-in-95 duration-300 space-y-6">
                        {/* Psychographic Hero Card */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700/50 flex flex-col justify-center items-center text-center">
                                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Dominant Driver</span>
                                <div className="text-lg font-bold text-white mb-1">{currentWardResonance.psychographic_driver || 'N/A'}</div>
                                <div className="text-xs text-slate-400">Primary decision-making heuristic</div>
                            </div>
                            <div className="bg-slate-800/60 p-4 rounded-xl border border-slate-700/50 flex flex-col justify-center items-center text-center">
                                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Cognitive Route</span>
                                <div className="flex items-center gap-2 mb-1">
                                    {currentWardResonance.cognitive_route === 'central_route' ? <BookOpen size={18} className="text-cyan-400" /> : <Eye size={18} className="text-fuchsia-400" />}
                                    <div className={`text-lg font-bold ${currentWardResonance.cognitive_route === 'central_route' ? 'text-cyan-400' : 'text-fuchsia-400'}`}>
                                        {currentWardResonance.cognitive_route === 'central_route' ? 'Central' : 'Peripheral'}
                                    </div>
                                </div>
                                <div className="text-xs text-slate-400">
                                    {currentWardResonance.cognitive_route === 'central_route' ? 'Logic & Argument Driven' : 'Emotion & Cue Driven'}
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-indigo-900/40 to-slate-800/60 p-4 rounded-xl border border-indigo-500/30 flex flex-col justify-center items-center text-center relative overflow-hidden">
                                <div className="absolute inset-0 bg-indigo-500/5 animate-pulse"></div>
                                <span className="text-[10px] uppercase tracking-widest text-indigo-300 font-bold mb-2 relative z-10">Global Synergy</span>
                                <div className="text-2xl font-black text-white mb-1 relative z-10">{currentWardResonance.global_synergy_score?.toFixed(2)}x</div>
                                <div className="text-xs text-indigo-200/70 relative z-10">Multi-channel multiplier</div>
                            </div>
                        </div>

                        {/* Deployment Directives */}
                        <div>
                            <h5 className="text-sm font-bold text-white flex items-center gap-2 mb-3">
                                <GitMerge size={16} className="text-slate-400" /> Deployment Directives
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {(currentWardResonance.deployment_directives || []).map((dir, idx) => (
                                    <div key={idx} className="bg-slate-800/40 border border-slate-700/50 p-3 rounded-lg hover:border-slate-600 transition-colors">
                                        <div className="flex justify-between items-start mb-2">
                                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-700 text-slate-300 border border-slate-600">{dir.type}</span>
                                            <span className="text-[10px] text-slate-500">Conf: {(dir.confidence_score * 100).toFixed(0)}%</span>
                                        </div>
                                        <p className="text-sm font-medium text-white mb-1 leading-snug">"{dir.instruction}"</p>
                                        <p className="text-xs text-slate-400 leading-relaxed mb-2">{dir.rationale}</p>
                                        <div className="text-[10px] text-slate-600 italic border-t border-slate-700/50 pt-2 mt-auto">Basis: {dir.scientific_basis}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Neuro-Risk Factors */}
                            {currentWardResonance.neuro_risk_factors?.length > 0 && (
                                <div className="bg-red-900/10 border border-red-500/20 rounded-lg p-4">
                                    <h6 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                        <AlertOctagon size={14} /> Neuro-Risk Factors
                                    </h6>
                                    <ul className="space-y-2">
                                        {currentWardResonance.neuro_risk_factors.map((risk, i) => (
                                            <li key={i} className="flex items-start gap-2 text-xs text-red-200/80">
                                                <span className="mt-1 w-1 h-1 rounded-full bg-red-500 shrink-0"></span>
                                                {risk}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* Top Synergies */}
                            <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
                                <h6 className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <Layers size={14} /> Top Interactions
                                </h6>
                                <div className="space-y-2">
                                    {(currentWardResonance.synergy_matrix || [])
                                        .filter(s => Math.abs(s.final_synergy_score) > 0.1)
                                        .slice(0, 3)
                                        .map((syn, i) => (
                                            <div key={i} className="flex justify-between items-center text-xs">
                                                <span className="text-slate-300">
                                                    {formatChannelName(syn.pair[0])} + {formatChannelName(syn.pair[1])}
                                                </span>
                                                <span className={`font-mono font-bold ${syn.final_synergy_score > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                    {syn.final_synergy_score > 0 ? '+' : ''}{syn.final_synergy_score.toFixed(2)}
                                                </span>
                                            </div>
                                        ))}
                                    {(currentWardResonance.synergy_matrix || []).filter(s => Math.abs(s.final_synergy_score) > 0.1).length === 0 && (
                                        <p className="text-xs text-slate-500 italic">No strong channel interactions detected.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StrategicOverviewPanel;
